'use client';

import { useEffect, useRef, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Calendar as CalendarIcon, Clock, CopyIcon, Loader2 } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { toast } from 'sonner';
import { isAxiosError } from 'axios';

import { cn } from '@/lib/utils';
import axiosInstance from '@/lib/axios';
import { FIELD_CHROME, type FieldVariant } from '@/lib/field-chrome';
import { useFieldVariant } from '@/components/ui/field-variant';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface DateTimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Visual variant. Defaults to the nearest `FieldVariantProvider`, or `'solid'`. */
  variant?: FieldVariant;
}

const DATETIME_PICKER_VARIANT = {
  solid: `${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusWithin}`,
  glass: `${FIELD_CHROME.glass.base} ${FIELD_CHROME.glass.focusWithin}`,
} as const;

function formatTimestamp(timestamp: string): string {
  if (!timestamp) return '';
  const dt = parseISO(timestamp);
  if (!isValid(dt)) return '';
  return format(dt, 'MMMM d, yyyy, h:mm a');
}

type Meridiem = 'AM' | 'PM';

const HOURS = Array.from({ length: 12 }, (_, index) => index + 1);
const MINUTES = Array.from({ length: 60 }, (_, index) => index);

function readClock(date: Date): { hour12: number; minute: number; period: Meridiem } {
  const hours = date.getHours();
  return {
    hour12: hours % 12 || 12,
    minute: date.getMinutes(),
    period: hours >= 12 ? 'PM' : 'AM',
  };
}

function withClock(date: Date, hour12: number, minute: number, period: Meridiem): Date {
  const next = new Date(date);
  const hours = (hour12 % 12) + (period === 'PM' ? 12 : 0);
  next.setHours(hours, minute, 0, 0);
  return next;
}

function ClockColumn({
  label,
  value,
  options,
  formatOption,
  onSelect,
}: {
  label: string;
  value: number;
  options: number[];
  formatOption: (option: number) => string;
  onSelect: (option: number) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const selectedNode = selectedRef.current;
    if (!list || !selectedNode) return;
    const top =
      selectedNode.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
    list.scrollTop = top - list.clientHeight / 2 + selectedNode.clientHeight / 2;
  }, []);

  return (
    <div className="space-y-1">
      <p className="text-center text-[11px] font-medium text-muted-foreground">{label}</p>
      <div
        ref={listRef}
        className="h-28 touch-pan-y overflow-y-auto rounded-xl border border-input bg-background p-1"
      >
        {options.map((option) => {
          const isSelected = option === value;
          return (
            <button
              key={option}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              onClick={() => onSelect(option)}
              className={cn(
                'flex h-8 w-full items-center justify-center rounded-lg text-sm font-medium',
                isSelected ? 'bg-primary text-white' : 'text-foreground hover:bg-muted'
              )}
            >
              {formatOption(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CalendarDateTimePanel({
  initialDate,
  onChange,
  onDone,
}: {
  initialDate?: Date;
  onChange: (date: Date) => void;
  onDone: () => void;
}) {
  const initialClock = initialDate
    ? readClock(initialDate)
    : { hour12: 9, minute: 0, period: 'AM' as const };
  const [selected, setSelected] = useState<Date | undefined>(initialDate);
  const [hour12, setHour12] = useState(initialClock.hour12);
  const [minute, setMinute] = useState(initialClock.minute);
  const [period, setPeriod] = useState<Meridiem>(initialClock.period);

  function apply(day: Date, nextHour = hour12, nextMinute = minute, nextPeriod = period) {
    const next = withClock(day, nextHour, nextMinute, nextPeriod);
    setSelected(next);
    onChange(next);
  }

  function updateClock(nextHour: number, nextMinute: number, nextPeriod: Meridiem) {
    setHour12(nextHour);
    setMinute(nextMinute);
    setPeriod(nextPeriod);
    if (selected) apply(selected, nextHour, nextMinute, nextPeriod);
  }

  return (
    <div className="flex flex-col">
      <Calendar
        mode="single"
        selected={selected}
        onSelect={(day) => {
          if (!day) return;
          apply(day);
        }}
        defaultMonth={selected}
        initialFocus
      />
      <div className="space-y-3 border-t border-border px-3 pb-3 pt-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          Time
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr_auto] items-stretch gap-2">
          <ClockColumn
            label="Hour"
            value={hour12}
            options={HOURS}
            formatOption={(option) => String(option)}
            onSelect={(option) => updateClock(option, minute, period)}
          />
          <span className="flex items-center text-sm font-semibold text-muted-foreground">:</span>
          <ClockColumn
            label="Minute"
            value={minute}
            options={MINUTES}
            formatOption={(option) => String(option).padStart(2, '0')}
            onSelect={(option) => updateClock(hour12, option, period)}
          />
          <div className="flex flex-col justify-center gap-1 rounded-xl bg-muted p-0.5">
            {(['AM', 'PM'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => updateClock(hour12, minute, option)}
                className={cn(
                  'rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors',
                  period === option
                    ? 'bg-primary text-white shadow-sm shadow-primary/20'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {selected
            ? format(selected, 'EEEE, MMMM d, yyyy, h:mm a')
            : 'Select a date, then a time.'}
        </p>
        <Button type="button" className="w-full" size="sm" onClick={onDone} disabled={!selected}>
          Set date and time
        </Button>
      </div>
    </div>
  );
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'e.g. next Monday at 6pm',
  disabled,
  className,
  variant,
}: DateTimePickerProps) {
  const resolvedVariant = useFieldVariant(variant);
  const initialFormatted = formatTimestamp(value ?? '');
  const [englishText, setEnglishText] = useState(initialFormatted);
  const [formattedDateTime, setFormattedDateTime] = useState(initialFormatted);
  const [isConverting, setIsConverting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Track whether the user is actively typing so we don't fight with the value prop.
  const isUpdatingFromInputRef = useRef(false);
  const lastValueRef = useRef(value);
  const requestIdRef = useRef(0);

  // Sync from external value changes (e.g. form reset, edit-page initial load).
  useEffect(() => {
    if (isUpdatingFromInputRef.current) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    const formatted = formatTimestamp(value ?? '');
    setFormattedDateTime(formatted);
    setEnglishText(formatted);
    setErrorMessage(null);
  }, [value]);

  const convertToTimestamp = useDebouncedCallback(async (text: string) => {
    const requestId = ++requestIdRef.current;
    if (!text.trim()) {
      isUpdatingFromInputRef.current = true;
      onChange('');
      setFormattedDateTime('');
      setErrorMessage(null);
      setIsConverting(false);
      requestAnimationFrame(() => {
        isUpdatingFromInputRef.current = false;
      });
      return;
    }

    try {
      setIsConverting(true);
      setErrorMessage(null);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axiosInstance.post<{ timestamp: string }>(
        '/api/ai/english-to-timestamp',
        { text, timezone }
      );
      const timestamp = response.data?.timestamp;
      if (requestId !== requestIdRef.current) return;
      if (!timestamp) {
        setErrorMessage("Couldn't understand that date/time. Try rephrasing.");
        return;
      }
      isUpdatingFromInputRef.current = true;
      lastValueRef.current = timestamp;
      onChange(timestamp);
      setFormattedDateTime(formatTimestamp(timestamp));
      requestAnimationFrame(() => {
        isUpdatingFromInputRef.current = false;
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (isAxiosError(error)) {
        const serverError = error.response?.data as { error?: string } | undefined;
        const message =
          error.response?.status === 503
            ? 'The AI service is busy. Try a simpler date like "tomorrow" or "next week".'
            : serverError?.error || 'Failed to interpret the date/time.';

        console.error('API Error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });

        setErrorMessage(message);
        if (error.response?.status !== 422 && error.response?.status !== 503) {
          toast.error(message);
        }
      } else {
        console.error('Unknown conversion error:', error);
        setErrorMessage('An unexpected error occurred.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsConverting(false);
      }
    }
  }, 700);

  function commitDateTime(date: Date) {
    const timestamp = date.toISOString();
    const formatted = formatTimestamp(timestamp);
    requestIdRef.current += 1;
    convertToTimestamp.cancel();
    isUpdatingFromInputRef.current = true;
    lastValueRef.current = timestamp;
    onChange(timestamp);
    setFormattedDateTime(formatted);
    setEnglishText(formatted);
    setErrorMessage(null);
    setIsConverting(false);
    requestAnimationFrame(() => {
      isUpdatingFromInputRef.current = false;
    });
  }

  const calendarDate = value ? parseISO(value) : undefined;
  const initialCalendarDate = calendarDate && isValid(calendarDate) ? calendarDate : undefined;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newText = e.target.value;
    setEnglishText(newText);
    setErrorMessage(null);
    convertToTimestamp(newText);
  }

  async function handleCopy() {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Timestamp copied');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }

  const showPreview =
    !errorMessage &&
    !!formattedDateTime &&
    englishText.trim().length > 0 &&
    formattedDateTime !== englishText.trim();

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-xl',
        DATETIME_PICKER_VARIANT[resolvedVariant],
        errorMessage &&
          'border-destructive focus-within:border-destructive focus-within:ring-destructive',
        className
      )}
    >
      <div className="flex items-center">
        <input
          type="text"
          value={englishText}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          className="h-11 w-full bg-transparent px-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-neutral-100 dark:placeholder:text-neutral-400"
        />
        {isConverting && (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        )}
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen} modal>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Open calendar"
              title="Pick date and time"
              disabled={disabled}
              className="mr-1.5 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-calendar-popover=""
            align="end"
            sideOffset={8}
            collisionPadding={{ top: 16, right: 12, bottom: 88, left: 12 }}
            className="pointer-events-auto z-[80] max-h-[var(--radix-popover-content-available-height)] w-auto max-w-[calc(100vw-1.5rem)] touch-pan-y overflow-y-auto overscroll-y-contain p-0"
          >
            {isCalendarOpen && (
              <CalendarDateTimePanel
                initialDate={initialCalendarDate}
                onChange={commitDateTime}
                onDone={() => setIsCalendarOpen(false)}
              />
            )}
          </PopoverContent>
        </Popover>
      </div>

      <AnimatePresence initial={false}>
        {errorMessage ? (
          <motion.div
            key="error"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="border-t border-destructive/40 bg-destructive/10 dark:bg-destructive/20"
          >
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{errorMessage}</span>
            </div>
          </motion.div>
        ) : showPreview ? (
          <motion.div
            key="preview"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="border-t border-zinc-300 bg-zinc-100 dark:border-zinc-700 dark:bg-neutral-900"
          >
            <div className="flex items-center justify-between gap-2 py-2 pl-4 pr-2">
              <span className="truncate text-xs text-muted-foreground dark:text-neutral-400">
                {formattedDateTime}
              </span>
              <motion.button
                whileTap={{ scale: 0.94 }}
                tabIndex={-1}
                type="button"
                onClick={handleCopy}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
              >
                <CopyIcon className="h-3.5 w-3.5" />
              </motion.button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
